import { apiClient } from '../apiClient';
import { apiPath } from '../apiPath';
import { CompanySettings } from '../types/company';
import { getCompanyRegistry } from '../company/companyRegistrySingleton';
import type { DayReport as RegistryDayReport } from '../company/CompanyRegistry';
import { getAccessRegistry } from '../auth/AccessRegistry';

/**
 * Fetches the day report for a given date and merges it into the CompanyRegistry singleton.
 * @param date - The date string (YYYY-MM-DD)
 * @returns The merged day report from the registry.
 */
export async function fetchDayReport(date: string): Promise<RegistryDayReport | null> {
  const { data } = await apiClient.get(apiPath(`/company/timetable/day-report/${date}`));
  const registry = getCompanyRegistry();
  registry.mergeDayReport(date, data.day_info);
  return registry.getDayReport(date);
}

/**
 * Fetches company settings from the backend API.
 * @returns A promise resolving to the company settings.
 */
export async function fetchCompanySettings(): Promise<CompanySettings> {
  const response = await apiClient.get(apiPath('/company/settings'));
  const settings = response.data as CompanySettings;
  
  // Update the registry with the fetched settings
  const registry = getCompanyRegistry();
  registry.hydrate(settings);

  // Hydrate RBAC limitations into the AccessRegistry (allowlist will filter)
  try {
    getAccessRegistry().setRawAccess((settings as any)?.access || {});
  } catch (e) {
    // non-fatal; defaults (deny-by-default) will apply if payload is missing/malformed
    // eslint-disable-next-line no-console
    console.warn("[fetchCompanySettings] unable to hydrate access payload", e);
  }
  
  return settings;
}
